import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Alert,
  IconButton, Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../AuthContext';
import { getHouse } from '../api';
import AdminLayout from './AdminLayout';
import PhotoManager from './PhotoManager';

function HouseForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { authHeaders } = useAuth();

  const [form, setForm] = useState({
    name: '', location: '', zillow_url: '', youtube_urls: [''], description: '',
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    getHouse(id).then(house => {
      setForm({
        name: house.name || '',
        location: house.location || '',
        zillow_url: house.zillow_url || '',
        youtube_urls: house.youtube_urls?.length
          ? house.youtube_urls
          : (house.youtube_url ? [house.youtube_url] : ['']),
        description: house.description || '',
      });
      setLoading(false);
    });
  }, [id, isEdit]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const toYoutubeUrls = (value) => value
    .map(url => url.trim())
    .filter(Boolean);

  const handleYoutubeUrlChange = (index, value) => {
    setForm(current => ({
      ...current,
      youtube_urls: current.youtube_urls.map((url, currentIndex) => (
        currentIndex === index ? value : url
      )),
    }));
  };

  const addYoutubeUrl = () => {
    setForm(current => ({
      ...current,
      youtube_urls: [...current.youtube_urls, ''],
    }));
  };

  const removeYoutubeUrl = (index) => {
    setForm(current => {
      const nextUrls = current.youtube_urls.filter((_, currentIndex) => currentIndex !== index);
      return {
        ...current,
        youtube_urls: nextUrls.length ? nextUrls : [''],
      };
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      name: form.name,
      location: form.location,
      zillow_url: form.zillow_url,
      youtube_urls: toYoutubeUrls(form.youtube_urls),
      description: form.description,
    };
    const res = await fetch(isEdit ? `/api/houses/${id}` : '/api/houses', {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
    setSaving(false);
    if (res.ok) {
      const data = await res.json();
      navigate(isEdit ? '/dashboard' : `/dashboard/house/${data.id}/edit`);
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Something went wrong.');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Typography color="text.secondary">Loading...</Typography>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Button
          component={Link}
          to="/dashboard"
          startIcon={<ArrowBackIcon />}
          sx={{ color: '#888', '&:hover': { color: '#f0f0f0' } }}
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight={700}>
          {isEdit ? 'Edit House' : 'New House'}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isEdit ? { xs: '1fr', lg: '480px 1fr' } : '1fr',
          gap: 4,
          alignItems: 'start',
        }}
      >
        {/* Form card */}
        <Paper
          sx={{
            backgroundColor: '#161616',
            border: '1px solid #222',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} mb={3}>
            Details
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2.5}>
            <TextField
              label="Name *"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Location"
              name="location"
              value={form.location}
              onChange={handleChange}
              fullWidth
              size="small"
              placeholder="e.g. Buford, GA"
            />
            <TextField
              label="Zillow URL"
              name="zillow_url"
              value={form.zillow_url}
              onChange={handleChange}
              fullWidth
              size="small"
              placeholder="https://zillow.com/..."
            />
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
                <Typography variant="body2" color="text.secondary">
                  Project Videos
                </Typography>
                <Button
                  type="button"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addYoutubeUrl}
                  sx={{ color: '#FFA13C' }}
                >
                  Add Video
                </Button>
              </Box>
              <Box display="flex" flexDirection="column" gap={1.25}>
                {form.youtube_urls.map((url, index) => (
                  <Box key={index} display="flex" gap={1} alignItems="flex-start">
                    <TextField
                      label={`YouTube URL ${index + 1}`}
                      value={url}
                      onChange={e => handleYoutubeUrlChange(index, e.target.value)}
                      fullWidth
                      size="small"
                      placeholder="https://youtube.com/watch?v=..."
                    />
                    <IconButton
                      type="button"
                      onClick={() => removeYoutubeUrl(index)}
                      disabled={form.youtube_urls.length === 1 && !url.trim()}
                      sx={{
                        mt: '2px',
                        border: '1px solid #2a2a2a',
                        borderRadius: 1.5,
                        color: '#999',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              size="small"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{
                mt: 1,
                py: 1.2,
                fontWeight: 600,
                backgroundColor: '#FFA13C',
                color: '#000',
                '&:hover': { backgroundColor: '#e8912e' },
              }}
            >
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create House & Add Photos'}
            </Button>
          </Box>
        </Paper>

        {/* Photo manager — only shown in edit mode */}
        {isEdit && (
          <Paper
            sx={{
              backgroundColor: '#161616',
              border: '1px solid #222',
              borderRadius: 2,
              p: 3,
            }}
          >
            <PhotoManager houseId={id} />
          </Paper>
        )}
      </Box>
    </AdminLayout>
  );
}

export default HouseForm;
